import React, { useMemo, useState } from 'react'
import { groupColor } from './theme'
import { formatSeconds, formatInches, formatWeight, formatCurrency, formatPct } from './formatters'

/**
 * Materials Bill — two procurement-friendly roll-ups for the Overview tab:
 *
 *   1. Material Requirements: one row per material group with sheet count,
 *      raw weight, scrap %, $/sheet, total cost. Footer rolls up totals.
 *      Customer-supplied groups are flagged so they don't get accidentally
 *      ordered.
 *
 *   2. Parts Manifest: one row per unique part (file name) with rolled-up
 *      quantity across every group it appears in, plus cut length, pierces,
 *      total weight, and total process time. Sortable.
 *
 * Each table has a Copy button that emits TSV text suitable for pasting into
 * Excel, Slack, or an email body. Parts Manifest is collapsible and is open
 * by default for projects under 30 parts; collapsed otherwise.
 */
export default function NesterMaterialsBill({
  colors,
  nestResult,
  materialGroups = [],
}) {
  const isMulti = nestResult?.group_results?.length > 0
  const groups = isMulti ? nestResult.group_results : (nestResult ? [nestResult] : [])

  // ── Build material rows
  const materialRows = useMemo(() => {
    return groups.map((g, i) => {
      const mg = materialGroups.find(m => m.group_id === g.material_group_id) || {}
      const cs = g.cost_summary || {}
      const sheets = g.sheets_needed || 0
      const rawWeight = cs.raw_sheet_weight_lbs || 0
      const partsWeight = cs.total_parts_weight_lbs || 0
      const materialCost = cs.material_cost || 0
      const customerSupplied = mg.customer_supplied_material === true
      const costPerSheet = customerSupplied
        ? 0
        : (sheets > 0 ? materialCost / sheets : 0)
      const sheetSize = mg.sheet_width && mg.sheet_length
        ? `${mg.sheet_width}×${mg.sheet_length}"`
        : '—'
      return {
        idx: i,
        accent: groupColor(i),
        materialType: mg.material_type || g.material_group_name || `Group ${i + 1}`,
        thickness: mg.thickness_display || '—',
        finish: mg.surface_finish || '—',
        sheetSize,
        sheets,
        rawWeight,
        partsWeight,
        scrapPct: cs.scrap_pct,
        costPerSheet,
        materialCost,
        customerSupplied,
      }
    })
  }, [groups, materialGroups])

  const matTotals = useMemo(() => {
    return materialRows.reduce((acc, r) => ({
      sheets: acc.sheets + r.sheets,
      rawWeight: acc.rawWeight + r.rawWeight,
      partsWeight: acc.partsWeight + r.partsWeight,
      materialCost: acc.materialCost + (r.customerSupplied ? 0 : r.materialCost),
      customerSuppliedCost: acc.customerSuppliedCost + (r.customerSupplied ? r.materialCost : 0),
      customerSuppliedSheets: acc.customerSuppliedSheets + (r.customerSupplied ? r.sheets : 0),
    }), {
      sheets: 0, rawWeight: 0, partsWeight: 0,
      materialCost: 0, customerSuppliedCost: 0, customerSuppliedSheets: 0,
    })
  }, [materialRows])

  // ── Build parts roll-up
  const [partsSort, setPartsSort] = useState({ key: 'quantity', dir: 'desc' })
  const partsRows = useMemo(() => {
    const map = new Map()
    groups.forEach((g, i) => {
      const accent = groupColor(i)
      const mg = materialGroups.find(m => m.group_id === g.material_group_id) || {}
      const groupLabel = mg.group_name
        || g.material_group_name
        || [mg.material_type, mg.thickness_display].filter(Boolean).join(' ')
        || `Group ${i + 1}`
      ;(g.parts_summary || []).forEach(p => {
        const key = `${g.material_group_id || 'single'}::${p.part_id}`
        const existing = map.get(key)
        if (existing) {
          existing.quantity += p.quantity || 0
          existing.totalCutLength += (p.cut_length_in || 0) * (p.quantity || 0)
          existing.totalPierces += (p.pierce_count || 0) * (p.quantity || 0)
          existing.totalWeightG += (p.weight_g || 0) * (p.quantity || 0)
          existing.totalProcessSec += (p.est_process_time_sec || 0) * (p.quantity || 0)
        } else {
          map.set(key, {
            partId: p.part_id,
            label: p.part_label || p.file_name,
            fileName: p.file_name,
            groupLabel,
            groupAccent: accent,
            sizeW: p.bounding_width,
            sizeH: p.bounding_height,
            unitCutLength: p.cut_length_in,
            unitPierces: p.pierce_count,
            unitWeightG: p.weight_g,
            unitProcessSec: p.est_process_time_sec,
            quantity: p.quantity || 0,
            totalCutLength: (p.cut_length_in || 0) * (p.quantity || 0),
            totalPierces: (p.pierce_count || 0) * (p.quantity || 0),
            totalWeightG: (p.weight_g || 0) * (p.quantity || 0),
            totalProcessSec: (p.est_process_time_sec || 0) * (p.quantity || 0),
            fillSheet: p.fill_sheet,
          })
        }
      })
    })

    const arr = [...map.values()]
    arr.sort((a, b) => {
      let av = a[partsSort.key], bv = b[partsSort.key]
      if (typeof av === 'string') {
        return partsSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      }
      av = av ?? 0; bv = bv ?? 0
      return partsSort.dir === 'asc' ? av - bv : bv - av
    })
    return arr
  }, [groups, materialGroups, partsSort])

  const partTotals = useMemo(() => {
    return partsRows.reduce((acc, r) => ({
      quantity: acc.quantity + r.quantity,
      totalCutLength: acc.totalCutLength + r.totalCutLength,
      totalPierces: acc.totalPierces + r.totalPierces,
      totalWeightG: acc.totalWeightG + r.totalWeightG,
      totalProcessSec: acc.totalProcessSec + r.totalProcessSec,
    }), { quantity: 0, totalCutLength: 0, totalPierces: 0, totalWeightG: 0, totalProcessSec: 0 })
  }, [partsRows])

  const [partsExpanded, setPartsExpanded] = useState(true)

  // Early-return AFTER all hooks (rules-of-hooks)
  if (!nestResult || groups.length === 0) return null

  // ── Copy-to-clipboard (TSV)
  function copyMaterials() {
    const header = ['Material', 'Thickness', 'Finish', 'Sheet Size', 'Sheets', 'Raw Weight (lb)', 'Parts Weight (lb)', 'Scrap %', '$/Sheet', 'Material Cost', 'Customer Supplied']
    const lines = [header.join('\t')]
    materialRows.forEach(r => {
      lines.push([
        r.materialType, r.thickness, r.finish, r.sheetSize,
        r.sheets,
        r.rawWeight.toFixed(2),
        r.partsWeight.toFixed(2),
        r.scrapPct != null ? r.scrapPct.toFixed(1) + '%' : '',
        r.customerSupplied ? '' : r.costPerSheet.toFixed(2),
        r.customerSupplied ? '' : r.materialCost.toFixed(2),
        r.customerSupplied ? 'YES' : '',
      ].join('\t'))
    })
    lines.push([
      'TOTAL', '', '', '',
      matTotals.sheets,
      matTotals.rawWeight.toFixed(2),
      matTotals.partsWeight.toFixed(2),
      '',
      '',
      matTotals.materialCost.toFixed(2),
      '',
    ].join('\t'))
    return lines.join('\n')
  }

  function copyParts() {
    const header = ['Part', 'Material Group', 'Qty', 'Size (in)', 'Unit Cut (in)', 'Unit Pierces', 'Total Cut (in)', 'Total Pierces', 'Total Weight (g)', 'Total Process Time']
    const lines = [header.join('\t')]
    partsRows.forEach(r => {
      lines.push([
        r.label,
        r.groupLabel,
        r.quantity,
        `${r.sizeW?.toFixed(2) || ''} x ${r.sizeH?.toFixed(2) || ''}`,
        r.unitCutLength?.toFixed(2) || '',
        r.unitPierces || '',
        r.totalCutLength.toFixed(1),
        r.totalPierces,
        r.totalWeightG.toFixed(1),
        formatSeconds(r.totalProcessSec),
      ].join('\t'))
    })
    lines.push([
      'TOTAL', '',
      partTotals.quantity, '', '', '',
      partTotals.totalCutLength.toFixed(1),
      partTotals.totalPierces,
      partTotals.totalWeightG.toFixed(1),
      formatSeconds(partTotals.totalProcessSec),
    ].join('\t'))
    return lines.join('\n')
  }

  function doCopy(text, label) {
    navigator.clipboard.writeText(text).then(
      () => { /* success — could show toast */ },
      () => alert(`Copy failed — paste manually:\n\n${text}`)
    )
  }

  function togglePartsSort(key) {
    setPartsSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' })
  }

  return (
    <div style={{ marginTop: 14 }}>
      {/* ── Material Requirements ────────────────────────────────── */}
      <div style={styles.section(colors)}>
        <div style={styles.sectionHeader(colors)}>
          <div>
            <div style={styles.sectionTitle(colors)}>Material Requirements</div>
            <div style={styles.sectionSub(colors)}>
              What you'll need to procure for this project
            </div>
          </div>
          <button onClick={() => doCopy(copyMaterials())}
            style={styles.copyBtn(colors)}>
            ⧉ Copy as TSV
          </button>
        </div>
        <div style={styles.tableWrap(colors)}>
          <table style={styles.table(colors)}>
            <thead>
              <tr style={styles.thead(colors)}>
                <th style={styles.th(colors)}></th>
                <th style={styles.th(colors)}>Material</th>
                <th style={styles.th(colors)}>Thickness</th>
                <th style={styles.th(colors)}>Finish</th>
                <th style={styles.th(colors)}>Sheet Size</th>
                <th style={styles.thRight(colors)}>Sheets</th>
                <th style={styles.thRight(colors)}>Raw Weight</th>
                <th style={styles.thRight(colors)}>Parts Weight</th>
                <th style={styles.thRight(colors)}>Scrap</th>
                <th style={styles.thRight(colors)}>$/Sheet</th>
                <th style={styles.thRight(colors)}>Material Cost</th>
              </tr>
            </thead>
            <tbody>
              {materialRows.map(r => (
                <tr key={r.idx}>
                  <td style={{ ...styles.td(colors), padding: '4px 6px', borderLeft: `4px solid ${r.accent}` }} />
                  <td style={styles.td(colors)}>
                    <div style={{ fontWeight: 600 }}>{r.materialType}</div>
                    {r.customerSupplied && (
                      <span style={styles.csTag(colors)}>Customer-supplied</span>
                    )}
                  </td>
                  <td style={styles.td(colors)}>{r.thickness}</td>
                  <td style={styles.td(colors)}>{r.finish}</td>
                  <td style={styles.td(colors)}>{r.sheetSize}</td>
                  <td style={styles.tdRight(colors)}>{r.sheets}</td>
                  <td style={styles.tdRight(colors)}>{formatWeight(r.rawWeight)}</td>
                  <td style={styles.tdRight(colors)}>{formatWeight(r.partsWeight)}</td>
                  <td style={styles.tdRight(colors)}>
                    <span style={{ color: r.scrapPct > 50 ? colors.danger : colors.text }}>
                      {formatPct(r.scrapPct)}
                    </span>
                  </td>
                  <td style={styles.tdRight(colors)}>
                    {r.customerSupplied ? <span style={{ color: colors.textFaint }}>—</span> : formatCurrency(r.costPerSheet)}
                  </td>
                  <td style={styles.tdRight(colors)}>
                    {r.customerSupplied
                      ? <span style={{ color: colors.textFaint, fontStyle: 'italic' }}>n/a</span>
                      : <strong>{formatCurrency(r.materialCost)}</strong>}
                  </td>
                </tr>
              ))}
              <tr style={styles.totalRow(colors)}>
                <td style={{ padding: '4px 6px' }} />
                <td style={styles.tdTotal(colors)} colSpan={4}>
                  TOTAL — to procure
                  {matTotals.customerSuppliedSheets > 0 && (
                    <span style={{ fontWeight: 400, fontSize: 11, color: colors.textMuted, marginLeft: 8 }}>
                      (excludes {matTotals.customerSuppliedSheets} customer-supplied sheet{matTotals.customerSuppliedSheets === 1 ? '' : 's'})
                    </span>
                  )}
                </td>
                <td style={styles.tdRightTotal(colors)}>{matTotals.sheets - matTotals.customerSuppliedSheets}</td>
                <td style={styles.tdRightTotal(colors)}>{formatWeight(matTotals.rawWeight)}</td>
                <td style={styles.tdRightTotal(colors)}>{formatWeight(matTotals.partsWeight)}</td>
                <td />
                <td />
                <td style={{ ...styles.tdRightTotal(colors), color: colors.accent }}>
                  {formatCurrency(matTotals.materialCost)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Parts Manifest ───────────────────────────────────────── */}
      <div style={{ ...styles.section(colors), marginTop: 14 }}>
        <div style={styles.sectionHeader(colors)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setPartsExpanded(v => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: colors.textMuted, fontSize: 11, padding: 0, width: 14,
              }}>
              {partsExpanded ? '▼' : '▶'}
            </button>
            <div>
              <div style={styles.sectionTitle(colors)}>
                Parts Manifest <span style={{ fontWeight: 400, color: colors.textMuted, fontSize: 12 }}>
                  · {partsRows.length} unique part{partsRows.length === 1 ? '' : 's'}
                  · {partTotals.quantity} total
                </span>
              </div>
              <div style={styles.sectionSub(colors)}>
                Every part this project will produce, rolled up by part and group
              </div>
            </div>
          </div>
          <button onClick={() => doCopy(copyParts())} style={styles.copyBtn(colors)}>
            ⧉ Copy as TSV
          </button>
        </div>
        {partsExpanded && (
          <div style={styles.tableWrap(colors)}>
            <table style={styles.table(colors)}>
              <thead>
                <tr style={styles.thead(colors)}>
                  <SortTh colors={colors} label="Part" k="label" sort={partsSort} onClick={togglePartsSort} />
                  <SortTh colors={colors} label="Material Group" k="groupLabel" sort={partsSort} onClick={togglePartsSort} />
                  <SortTh colors={colors} label="Qty" k="quantity" sort={partsSort} onClick={togglePartsSort} numeric />
                  <th style={styles.thRight(colors)}>Size</th>
                  <SortTh colors={colors} label="Total Cut" k="totalCutLength" sort={partsSort} onClick={togglePartsSort} numeric />
                  <SortTh colors={colors} label="Total Pierces" k="totalPierces" sort={partsSort} onClick={togglePartsSort} numeric />
                  <SortTh colors={colors} label="Total Weight" k="totalWeightG" sort={partsSort} onClick={togglePartsSort} numeric />
                  <SortTh colors={colors} label="Total Process" k="totalProcessSec" sort={partsSort} onClick={togglePartsSort} numeric />
                </tr>
              </thead>
              <tbody>
                {partsRows.map(r => (
                  <tr key={`${r.groupLabel}::${r.partId}`}>
                    <td style={styles.td(colors)}>
                      <div style={{ fontWeight: 600 }}>{r.label}</div>
                      {r.fillSheet && (
                        <span style={{ fontSize: 10, color: colors.accent, fontWeight: 700 }}>
                          (Fill Sheet)
                        </span>
                      )}
                    </td>
                    <td style={styles.td(colors)}>
                      <span style={{
                        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                        background: r.groupAccent, marginRight: 6,
                      }} />
                      {r.groupLabel}
                    </td>
                    <td style={{ ...styles.tdRight(colors), fontWeight: 700 }}>{r.quantity}</td>
                    <td style={styles.tdRight(colors)}>
                      {r.sizeW && r.sizeH ? `${r.sizeW.toFixed(2)} × ${r.sizeH.toFixed(2)}` : '—'}
                    </td>
                    <td style={styles.tdRight(colors)}>{formatInches(r.totalCutLength, 1)}</td>
                    <td style={styles.tdRight(colors)}>{r.totalPierces}</td>
                    <td style={styles.tdRight(colors)}>
                      {r.totalWeightG > 0 ? `${r.totalWeightG.toFixed(0)} g` : '—'}
                    </td>
                    <td style={styles.tdRight(colors)}>{formatSeconds(r.totalProcessSec)}</td>
                  </tr>
                ))}
                <tr style={styles.totalRow(colors)}>
                  <td style={styles.tdTotal(colors)} colSpan={2}>TOTAL</td>
                  <td style={{ ...styles.tdRightTotal(colors), color: colors.accent }}>{partTotals.quantity}</td>
                  <td />
                  <td style={styles.tdRightTotal(colors)}>{formatInches(partTotals.totalCutLength, 0)}</td>
                  <td style={styles.tdRightTotal(colors)}>{partTotals.totalPierces}</td>
                  <td style={styles.tdRightTotal(colors)}>
                    {partTotals.totalWeightG > 0 ? `${(partTotals.totalWeightG / 453.592).toFixed(1)} lb` : '—'}
                  </td>
                  <td style={styles.tdRightTotal(colors)}>{formatSeconds(partTotals.totalProcessSec)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function SortTh({ colors, label, k, sort, onClick, numeric }) {
  const active = sort.key === k
  return (
    <th style={{
      ...(numeric ? styles.thRight(colors) : styles.th(colors)),
      cursor: 'pointer', userSelect: 'none',
    }}
      onClick={() => onClick(k)}>
      {label}
      {active && <span style={{ marginLeft: 4, fontSize: 9 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  )
}

const styles = {
  section: (c) => ({
    background: c.panel,
    border: `1px solid ${c.border}`,
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: c.shadow,
  }),
  sectionHeader: (c) => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    padding: '10px 14px',
    background: c.panelAlt,
    borderBottom: `1px solid ${c.border}`,
  }),
  sectionTitle: (c) => ({
    fontSize: 13, fontWeight: 700, color: c.text,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  }),
  sectionSub: (c) => ({
    fontSize: 11, color: c.textMuted, marginTop: 2,
  }),
  copyBtn: (c) => ({
    padding: '4px 10px', fontSize: 11, fontWeight: 600,
    border: `1px solid ${c.border}`, background: c.panel, color: c.text,
    borderRadius: 4, cursor: 'pointer',
    whiteSpace: 'nowrap',
  }),
  tableWrap: (c) => ({
    overflowX: 'auto',
  }),
  table: (c) => ({
    width: '100%', borderCollapse: 'collapse', fontSize: 13,
    color: c.text,
  }),
  thead: (c) => ({
    background: c.panelAlt,
  }),
  th: (c) => ({
    padding: '6px 10px', textAlign: 'left',
    fontSize: 11, fontWeight: 700, color: c.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: `1px solid ${c.border}`,
    whiteSpace: 'nowrap',
  }),
  thRight: (c) => ({
    padding: '6px 10px', textAlign: 'right',
    fontSize: 11, fontWeight: 700, color: c.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: `1px solid ${c.border}`,
    whiteSpace: 'nowrap',
  }),
  td: (c) => ({
    padding: '6px 10px',
    borderBottom: `1px solid ${c.border}`,
    color: c.text, verticalAlign: 'top',
  }),
  tdRight: (c) => ({
    padding: '6px 10px', textAlign: 'right',
    borderBottom: `1px solid ${c.border}`,
    color: c.text,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  }),
  tdTotal: (c) => ({
    padding: '7px 10px', textAlign: 'left',
    borderTop: `2px solid ${c.borderStrong}`,
    fontWeight: 700, fontSize: 13,
    color: c.text,
  }),
  tdRightTotal: (c) => ({
    padding: '7px 10px', textAlign: 'right',
    borderTop: `2px solid ${c.borderStrong}`,
    fontWeight: 700, fontSize: 13,
    color: c.text,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  }),
  totalRow: (c) => ({
    background: c.panelAlt,
  }),
  csTag: (c) => ({
    display: 'inline-block', marginTop: 2,
    padding: '1px 6px', borderRadius: 8,
    fontSize: 9, fontWeight: 700,
    background: c.successSoft, color: c.success,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  }),
}
